import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { readWizardStorage, updateWizardStorage } from "../../services/wizardStorage";

const WizardContext = createContext(null);
const GOAL_WIZARD_STORAGE_KEY = "goalWizard";

const getSavedProgress = (agentId, year) => {
  const saved = readWizardStorage();
  const s = saved.teamProductionWizard;
  if (
    saved.status === "in_progress" &&
    s &&
    String(s.year) === String(year) &&
    String(s.agentId ?? "") === String(agentId ?? "")
  ) {
    return s;
  }
  return null;
};

export const WizardProvider = ({ year, show, onClose, wizardData, agentId = null, children }) => {
  const { actions, goals } = wizardData ?? {};
  const scopeKey = `${year}:${String(agentId ?? "self")}`;
  const lastScopeKeyRef = useRef(scopeKey);
  const [page, setPage] = useState(1);
  const [currentNetIncomeGoal, setCurrentNetIncomeGoal] = useState("");
  const [workingMonths, setWorkingMonths] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [shouldCelebrateCompletion, setShouldCelebrateCompletion] = useState(false);
  const [goalWizard, _setGoalWizard] = useState(() => readWizardStorage());

  const setGoalWizard = useCallback((value) => {
    _setGoalWizard((prev) =>
      updateWizardStorage((current) =>
        typeof value === "function" ? value({ ...prev, ...current }) : value,
      ),
    );
  }, []);

  const persistWizardProgress = useCallback(
    ({ nextPage = page, nextGoals = goals, status = "in_progress" } = {}) => {
      const goalToSave = nextGoals?.currentNetIncomeGoal ?? currentNetIncomeGoal;
      setGoalWizard((existing) => ({
        ...existing,
        year,
        status,
        teamProductionWizard: {
          agentId,
          year,
          page: nextPage,
          currentNetIncomeGoal: goalToSave,
          workingMonths,
          goals: nextGoals,
        },
      }));
    },
    [agentId, currentNetIncomeGoal, goals, page, setGoalWizard, workingMonths, year],
  );

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === GOAL_WIZARD_STORAGE_KEY) {
        _setGoalWizard(readWizardStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!show) {
      setFormErrors({});
      setShouldCelebrateCompletion(false);
      return;
    }

    const scopeChanged = lastScopeKeyRef.current !== scopeKey;
    lastScopeKeyRef.current = scopeKey;

    const saved = getSavedProgress(agentId, year);
    if (saved) {
      setPage(saved.page || 1);
      setCurrentNetIncomeGoal(saved.currentNetIncomeGoal ?? "");
      setWorkingMonths(saved.workingMonths !== undefined ? String(saved.workingMonths) : "");
      setFormErrors({});
      setShouldCelebrateCompletion(false);
      return;
    }

    setPage(1);
    setFormErrors({});
    setShouldCelebrateCompletion(false);
    if (scopeChanged) {
      setCurrentNetIncomeGoal("");
      setWorkingMonths("");
    }
  }, [show, agentId, scopeKey, year]);

  // Pre-fill page 1 inputs from API when there is no saved progress for this agent/year.
  useEffect(() => {
    if (!show) return;

    const saved = getSavedProgress(agentId, year);
    if (saved) return;
    if (wizardData?.loading) return;
    if (wizardData?.loadedScopeKey !== scopeKey) return;

    setCurrentNetIncomeGoal(goals?.currentNetIncomeGoal || "");
    if (goals?.workingMonths !== undefined && goals?.workingMonths !== null) {
      setWorkingMonths(String(goals.workingMonths));
    }
  }, [
    agentId,
    goals?.currentNetIncomeGoal,
    goals?.workingMonths,
    scopeKey,
    show,
    wizardData?.loadedScopeKey,
    wizardData?.loading,
    year,
  ]);

  const goToPage = useCallback(
    (n) => {
      setPage(n);
      persistWizardProgress({ nextPage: n });
    },
    [persistWizardProgress],
  );

  const saveProgress = useCallback(
    ({ nextPage = page, nextGoals = goals } = {}) => {
      persistWizardProgress({ nextPage, nextGoals, status: "in_progress" });
      setPage(nextPage);
    },
    [goals, page, persistWizardProgress],
  );

  const completeWizard = useCallback(
    ({ nextGoals = goals } = {}) => {
      setShouldCelebrateCompletion(true);
      persistWizardProgress({ nextPage: 1, nextGoals, status: "completed" });
      setPage(8);
    },
    [goals, persistWizardProgress],
  );

  const validateAndNext = useCallback(async () => {
    if (page === 1) {
      const newErrors = {};

      if (!currentNetIncomeGoal) {
        newErrors.currentNetIncomeGoal = "Please enter your net income goal";
      }

      if (Object.keys(newErrors).length > 0) {
        setFormErrors(newErrors);
        return;
      }

      setFormErrors({});
      const nextGoals = { ...goals, currentNetIncomeGoal };
      actions?.setLocalGoals(nextGoals);

      const isSaved = await actions?.saveWizardStepOne?.({
        currentNetIncomeGoal,
      });
      if (isSaved === false) return;

      persistWizardProgress({ nextPage: 2, nextGoals });
      setPage(2);
      setTimeout(() => {
        persistWizardProgress({ nextPage: 3, nextGoals });
        setPage(3);
      }, 2000);
      return;
    }

    if (page >= 3 && page < 7) {
      const nextPage = page + 1;
      persistWizardProgress({ nextPage });
      setPage(nextPage);
      return;
    }

    if (page === 7) {
      completeWizard();
    }
  }, [actions, completeWizard, currentNetIncomeGoal, goals, page, persistWizardProgress]);

  const contextValue = useMemo(
    () => ({
      year,
      agentId,
      show,
      onClose,
      wizardData,
      page,
      goToPage,
      currentNetIncomeGoal,
      setCurrentNetIncomeGoal,
      workingMonths,
      setWorkingMonths,
      formErrors,
      shouldCelebrateCompletion,
      saveProgress,
      completeWizard,
      validateAndNext,
      goalWizard,
      setGoalWizard,
    }),
    [
      year,
      agentId,
      show,
      onClose,
      wizardData,
      page,
      goToPage,
      currentNetIncomeGoal,
      workingMonths,
      formErrors,
      shouldCelebrateCompletion,
      saveProgress,
      completeWizard,
      validateAndNext,
      goalWizard,
      setGoalWizard,
    ],
  );

  return <WizardContext.Provider value={contextValue}>{children}</WizardContext.Provider>;
};

WizardProvider.propTypes = {
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  wizardData: PropTypes.shape().isRequired,
  agentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
};

export const useWizardContext = () => useContext(WizardContext);
